/* eslint-disable no-nested-ternary */
import { ChangeDetectionStrategy, Component, HostBinding, Input, OnInit } from '@angular/core';
import { distinctUntilChanged, filter, takeUntil } from 'rxjs';

import { PieceProductSettings } from 'extensions/core/piece-product/front/models/piece-product.models';
import { PieceProductCarouselSettings } from 'extensions/core/piece-product-carousel/front/models/piece-product-carousel.model';
import { Product } from 'idt-ecommerce-api/catalog/products/products.models';
import { ContextService } from 'idt-ecommerce-api/context/context.service';
import { StoreLanguage } from 'idt-ecommerce-api/stores/stores.models';
import { UnsubscribeComponent } from 'shared/directives/unsubscribe.component';
import { IdtComponent } from 'shared/models/idt-components/idt-component.models';
import {
  PRODUCT_DEFAULT_PRODUCT_PRICE_FORMAT,
  PRODUCT_DEFAULT_OLD_PRODUCT_PRICE_FORMAT,
  PRODUCT_DEFAULT_FUTURE_PRODUCT_PRICE_FORMAT,
  PRODUCT_DEFAULT_PRODUCT_PREWARMING_DESCRIPTION_FORMAT,
  PRODUCT_DEFAULT_ORIGINAL_PRODUCT_PRICE_FORMAT,
  PRODUCT_DEFAULT_PRICE_DISCOUNT_FORMAT
} from 'shared/models/product-prices/product-prices.models';
import { ProductsPricesService } from 'shared/services/products/products-prices.service';

import { templateUrl, styleUrls } from './render-product-prices.component.paths';


@Component({
  selector: 'render-product-prices',
  templateUrl,
  styleUrls,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RenderProductPricesComponent extends UnsubscribeComponent implements OnInit {

  @Input() public piece: IdtComponent<PieceProductCarouselSettings | PieceProductSettings> | undefined;
  @Input() public product!: Product;
  public direction: 'ltr' | 'rtl' = 'ltr';

  constructor(
    private readonly productsPricesService: ProductsPricesService,
    private readonly contextService: ContextService
  ) {
    super();
  }

  @HostBinding('style.--prices-flex-direction')
  public get displayProductPricesInTwoLines(): 'column' | 'row' {
    return (this.piece!.settings!.general as PieceProductCarouselSettings['general']).displayProductMultiplePricesTwoLines === true
      ? 'column'
      : 'row';
  }

  public get isVisibleOriginalPrice(): boolean {
    return !!this.piece!.settings!.general.displayOriginalProductPrice
      && !!this.product.priceInfo?.originalPriceWithCurrencySymbol;
  }

  public get isVisibleOldPrice(): boolean {
    return !!this.piece!.settings!.general.displayOldProductPrices
     && !!this.product.priceInfo?.oldPriceWithCurrencySymbol;
  }

  public get isVisibleFuturePrice(): boolean {
    return !!this.piece!.settings!.general.displayFutureProductPrices
      && this.isVisiblePrewarmingWhenDiscountApplied
      && !!this.product.priceInfo?.prewarming?.priceWithCurrency;
  }

  public get isVisiblePrewarmingDescription(): boolean {
    return !!this.piece!.settings!.general.displayProductPrewarmingDescription
      && this.isVisiblePrewarmingWhenDiscountApplied
      && !!this.product.priceInfo?.prewarming?.description;
  }

  public get isVisiblePrewarmingDiscountRate(): boolean {
    return !!this.piece!.settings!.general.displayProductPrewarmingDiscountRate
      && this.isVisiblePrewarmingWhenDiscountApplied
      && !!this.product.priceInfo?.prewarming?.discountRate;
  }

  public get isVisiblePrewarmingWhenDiscountApplied(): boolean {
    const hasOldPrice = !!this.product.priceInfo?.oldPrice;
    const shouldHidePrewarming = this.piece?.settings?.general.hidePrewarmingWhenDiscountApplied;
    return !hasOldPrice || (hasOldPrice && !shouldHidePrewarming);
  }

  public get isVisibleProductPriceDiscount(): boolean {
    return this.isVisibleOldPrice
      && !!this.piece!.settings!.general.displayPriceDiscount;
  }

  public get isVisiblePriceDescription(): boolean {
    return this.isVisibleOriginalPrice && !!this.piece!.settings!.general.displayProductPriceDescription;
  }

  public ngOnInit(): void {
    this.contextService.getCurrentLanguage$().pipe(
      distinctUntilChanged(),
      filter((currentLang: StoreLanguage) => !!currentLang?.direction),
      takeUntil(this.clearSubscriptions$)
    ).subscribe((currentLang: StoreLanguage) => {
      this.direction = currentLang.direction;
    });
  }

  public getTextProductPrice(isLocale: boolean, isCurrentPriceOnSale: boolean = false): string {
    return this.productsPricesService.getTextFormat(
      PRODUCT_DEFAULT_PRODUCT_PRICE_FORMAT,
      'productPrice',
      isLocale
        ? this.product.priceInfo!.localePriceWithCurrencySymbol!
        : this.product.priceInfo!.priceWithCurrencySymbol!,
      isCurrentPriceOnSale
        ? this.piece!.settings!.general.productPriceOnSaleTextFormat
        : this.piece!.settings!.general.productPricesTextFormat
    );
  }

  public getTextOldProductPrice(isLocale: boolean): string {
    return this.productsPricesService.getTextFormat(
      PRODUCT_DEFAULT_OLD_PRODUCT_PRICE_FORMAT,
      'oldProductPrice',
      isLocale
        ? this.product.priceInfo!.oldLocalePriceWithCurrencySymbol!
        : this.product.priceInfo!.oldPriceWithCurrencySymbol!,
      this.piece!.settings!.general.oldProductPricesTextFormat
    );
  }

  public getTextFutureProductPrice(isLocale: boolean): string {
    return this.productsPricesService.getTextFormat(
      PRODUCT_DEFAULT_FUTURE_PRODUCT_PRICE_FORMAT,
      'futureProductPrice',
      isLocale
        ? this.product.priceInfo!.prewarming!.localePriceWithCurrency!
        : this.product.priceInfo!.prewarming!.priceWithCurrency,
      this.piece!.settings!.general.futureProductPricesTextFormat
    );
  }

  public getTextProductPrewarmingDescription(): string {
    return this.productsPricesService.getTextFormat(
      PRODUCT_DEFAULT_PRODUCT_PREWARMING_DESCRIPTION_FORMAT,
      'productPrewarmingDescription',
      this.product.priceInfo!.prewarming!.description!,
      this.piece!.settings!.general.productPrewarmingDescriptionTextFormat
    );
  }

  public getTextOriginalProductPrice(): string {
    return this.productsPricesService.getTextFormat(
      PRODUCT_DEFAULT_ORIGINAL_PRODUCT_PRICE_FORMAT,
      'originalProductPrice',
      this.product.priceInfo!.originalPriceWithCurrencySymbol!,
      this.piece!.settings!.general.originalProductPriceTextFormat
    );
  }


  public getTextProductPrewarmingDiscountRate(): string {
    return this.productsPricesService.calculatePrewarmingDiscount(
      this.piece!.settings!.general.prewarmingDiscountOption,
      this.piece!.settings!.general.productPrewarmingDiscountRateTextFormat,
      this.product
    );
  }

  public getProductPriceDiscountTextFormat(): string {
    if (this.product.priceInfo?.discountsPercentages) {
      const discount = this.piece!.settings!.general.priceDiscountOption === 'old-price'
        ? this.product.priceInfo?.discountsPercentages?.oldPriceDiscount
        : this.product.priceInfo?.discountsPercentages?.originalPriceDiscount;
      const template = this.piece!.settings!.general.priceDiscountTextFormat ?? PRODUCT_DEFAULT_PRICE_DISCOUNT_FORMAT;
      const format = discount ? this.direction === 'ltr' ? `-${discount}%` : `${discount}%-` : '';
      return template.replace(/{{productPriceDiscount}}/g, format);
    }
    return this.productsPricesService.calculateActualPriceDiscount(
      this.piece!.settings!.general.priceDiscountOption,
      this.piece!.settings!.general.priceDiscountTextFormat,
      this.product
    );
  }

}
